import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ArrowUpDown, Bell, BellRing, ExternalLink, Heart, Store, Trash2 } from "lucide-react";
import { api, type Game } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWishlist } from "@/lib/wishlist";
import { usePriceAlerts } from "@/lib/price-alerts";
import { useSettings } from "@/lib/settings";

export const Route = createFileRoute("/_site/wishlist")({
  head: () => ({
    meta: [
      { title: "Your wishlist — NexKey.gg" },
      { name: "description", content: "All the games you saved on NexKey, with live price comparison." },
      { property: "og:title", content: "Your wishlist — NexKey.gg" },
      { property: "og:description", content: "All the games you saved on NexKey, with live price comparison." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: WishlistPage,
});

type SortKey = "discount" | "priceAsc" | "priceDesc" | "title";
const SORTS: SortKey[] = ["discount", "priceAsc", "priceDesc", "title"];

function currentPriceOf(g: Game): number | null {
  return g.bestOffer ?? g.steamPriceFinal ?? null;
}

function discountOf(g: Game): number {
  return g.savingsPercent ?? g.steamDiscountPercent ?? 0;
}

function WishlistPage() {
  const { t, formatPrice } = useSettings();
  const { isLoggedIn, loading: authLoading } = useAuth();
  const { ids, ready, toggle } = useWishlist();
  const alerts = usePriceAlerts();
  const [sort, setSort] = useState<SortKey>("discount");

  const remote = useQuery({
    queryKey: ["favorites", "remote", ids.join(",")],
    queryFn: () => api.favorites().then((r) => r.data),
    enabled: isLoggedIn && ready,
  });

  const local = useQuery({
    queryKey: ["favorites", "local", ids.join(",")],
    queryFn: () => api.gamesByIds(ids).then((r) => r.data),
    enabled: !isLoggedIn && ready,
  });

  const raw = isLoggedIn ? remote.data ?? [] : local.data ?? [];
  const loading = authLoading || !ready || (isLoggedIn ? remote.isLoading : local.isLoading);

  // Keep the list in sync with optimistic removals without waiting for a refetch.
  const games = useMemo(() => {
    const set = new Set(ids);
    const list = raw.filter((g) => set.has(g.id));
    const cmp: Record<SortKey, (a: Game, b: Game) => number> = {
      discount: (a, b) => discountOf(b) - discountOf(a),
      priceAsc: (a, b) => (currentPriceOf(a) ?? Infinity) - (currentPriceOf(b) ?? Infinity),
      priceDesc: (a, b) => (currentPriceOf(b) ?? -1) - (currentPriceOf(a) ?? -1),
      title: (a, b) => a.title.localeCompare(b.title),
    };
    return [...list].sort(cmp[sort]);
  }, [raw, ids, sort]);

  return (
    <div className="container mx-auto px-4 py-10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-2xl font-bold">
            <Heart className="h-5 w-5 text-primary" /> {t("nav.wishlist")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("wishlist.count").replace("{count}", String(loading ? 0 : games.length))}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isLoggedIn && !authLoading && (
            <Link
              to="/account/login"
              className="rounded-md border border-border bg-surface px-3 py-2 text-xs font-semibold hover:border-primary/60"
            >
              {t("wishlist.syncHint")}
            </Link>
          )}
          <label className="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-xs">
            <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">{t("wishlist.sort")}</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
              className="bg-transparent font-semibold outline-none"
              aria-label={t("wishlist.sort")}
            >
              {SORTS.map((s) => (
                <option key={s} value={s} className="bg-card">{t(`wishlist.sort.${s}`)}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-muted-foreground">{t("account.loading")}</div>
      ) : games.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-border bg-card p-10 text-center">
          <Heart className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">{t("wishlist.empty")}</p>
          <Link
            to="/games"
            className="mt-5 inline-flex rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110"
          >
            {t("hero.browseGames")}
          </Link>
        </div>
      ) : (
        <ul className="mt-8 space-y-3">
          {games.map((g) => {
            const best = g.bestOffer ?? null;
            const steam = g.steamPriceFinal ?? null;
            const current = currentPriceOf(g);
            const discount = discountOf(g);
            const stores = g.storeCount ?? 0;
            const alert = alerts.forGame(g.id);
            return (
              <li
                key={g.id}
                className="group grid grid-cols-[96px_1fr] gap-4 rounded-2xl border border-border bg-card p-3 transition hover:border-primary/50 sm:grid-cols-[160px_1fr_auto] sm:items-center sm:p-4"
              >
                <Link to="/game/$slug" params={{ slug: g.slug }} className="block overflow-hidden rounded-lg bg-muted">
                  <div className="aspect-[460/215]">
                    {g.headerImage ? (
                      <img src={g.headerImage} alt={g.title} loading="lazy" className="h-full w-full object-cover transition group-hover:scale-105" />
                    ) : (
                      <div className="grid h-full place-items-center text-[10px] text-muted-foreground">No image</div>
                    )}
                  </div>
                </Link>

                <div className="min-w-0">
                  <Link to="/game/$slug" params={{ slug: g.slug }} className="line-clamp-1 font-semibold hover:text-primary">
                    {g.title}
                  </Link>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span>
                      <span className="uppercase tracking-wider">{t("wishlist.bestOffer")}: </span>
                      <span className="font-display text-base font-bold text-primary">
                        {current != null ? (current === 0 ? "Free" : formatPrice(current, g.steamCurrency)) : t("wishlist.noOffers")}
                      </span>
                      {g.bestOfferCouponCode && (
                        <span className="ml-1.5 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                          {t("price.withCode")} {g.bestOfferCouponCode}
                        </span>
                      )}
                    </span>
                    {steam != null && best != null && (
                      <span>
                        <span className="uppercase tracking-wider">{t("wishlist.reference")}: </span>
                        <span className={steam > best ? "line-through" : ""}>{formatPrice(steam, g.steamCurrency)}</span>
                      </span>
                    )}
                    {discount > 0 && (
                      <span className="rounded-md bg-success px-2 py-0.5 text-[11px] font-bold text-success-foreground">-{discount}%</span>
                    )}
                    <span className="inline-flex items-center gap-1">
                      <Store className="h-3 w-3" />
                      {stores === 1 ? t("wishlist.store") : t("wishlist.stores").replace("{count}", String(stores))}
                    </span>
                    {alert && (
                      <span
                        className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                          alert.triggered ? "bg-success/15 text-success" : "bg-primary/10 text-primary"
                        }`}
                      >
                        {alert.triggered ? <BellRing className="h-3 w-3" /> : <Bell className="h-3 w-3" />}
                        {alert.triggered
                          ? t("wishlist.alertHit")
                          : t("wishlist.alertActive").replace("{price}", formatPrice(alert.targetPrice, alert.currency))}
                      </span>
                    )}
                  </div>
                </div>

                <div className="col-span-2 flex gap-2 sm:col-span-1 sm:flex-col">
                  <Link
                    to="/game/$slug"
                    params={{ slug: g.slug }}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:brightness-110 sm:flex-none"
                  >
                    <ExternalLink className="h-3.5 w-3.5" /> {t("wishlist.open")}
                  </Link>
                  <button
                    type="button"
                    onClick={() => toggle(g.id)}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md border border-border bg-surface px-3 py-2 text-xs font-semibold text-muted-foreground hover:border-destructive/60 hover:text-destructive sm:flex-none"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> {t("wishlist.remove")}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
